import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { IGroupRevenueAggregates, useGroupRevenueAggregates } from 'core/hooks';

import { formatMoneyCurrency } from '../../../_detailShared/format';
import { IOverviewWidgetProps } from './types';
import { OverviewWidgetShell } from './OverviewWidget';
import {
  CurrencyCode,
  CurrencyRow,
  Metric,
  MetricLabel,
  MetricValue,
} from './overview.styled';

/**
 * OverviewMoneyWidget (Requirement 1) — the money health card on the reworked
 * Overview tab.
 *
 * Fed by `useGroupRevenueAggregates(groupId)` — the same server-side per-currency
 * rollup the KPI strip uses — and renders revenue (paid) and debt per currency
 * without summing across currencies, consistent with the multi-currency money
 * model (Requirement 1.2). Clicking (or pressing Enter/Space) navigates to the
 * Finances tab (Requirement 2.4).
 */
export const OverviewMoneyWidget = memo(
  ({ groupId, onNavigate }: IOverviewWidgetProps) => {
    const { t } = useTranslation();
    const { data, loading } = useGroupRevenueAggregates(groupId);

    const rows = (data?.rows || []).filter((r): r is NonNullable<typeof r> =>
      Boolean(r),
    );

    return (
      <OverviewWidgetShell<IGroupRevenueAggregates>
        title={t('pages.groupDetail.overview.moneyTitle')}
        ariaLabel={t('pages.groupDetail.overview.openFinances')}
        state={{ data, loading, isEmpty: rows.length === 0 }}
        onActivate={() => onNavigate('finances')}
      >
        {() =>
          rows.map((r) => {
            const currency = r.currency || '';
            const paid = Number(r.paid) || 0;
            const debt = Number(r.debt) || 0;
            return (
              <CurrencyRow key={currency || '—'}>
                <CurrencyCode>{currency || '—'}</CurrencyCode>
                <Metric>
                  <MetricLabel>
                    {t('pages.groupDetail.kpi.revenue')}
                  </MetricLabel>
                  <MetricValue tone="success">
                    {formatMoneyCurrency(paid, currency)}
                  </MetricValue>
                </Metric>
                <Metric>
                  <MetricLabel>{t('pages.groupDetail.kpi.debt')}</MetricLabel>
                  <MetricValue tone={debt > 0 ? 'warn' : undefined}>
                    {formatMoneyCurrency(debt, currency)}
                  </MetricValue>
                </Metric>
              </CurrencyRow>
            );
          })
        }
      </OverviewWidgetShell>
    );
  },
);
